import { create } from 'zustand';
import { authService } from '@/core/api/auth/auth.service';
import { LOCALSTORAGE } from '@/constants/localstorage';
import storage from '@/shared/utils/storage/storage';
import type { AuthState } from '@/core/types/auth';
import type { DocumentPropsTypes } from '@/core/types/documents';
import { queryClient } from '@/core/query/query.client';

type StorageDriver = 'local' | 'session';

interface HydrateOptions {
    silent?: boolean;
}

const AUTH_KEYS = [
    LOCALSTORAGE.accessToken,
    LOCALSTORAGE.refreshToken,
    LOCALSTORAGE.user,
    LOCALSTORAGE.menu,
    LOCALSTORAGE.documents,
] as const;

const createDefaultAuthState = (): AuthState => ({
    isAuthenticated: false,
    accessToken: null,
    refreshToken: null,
    user: null,
    menu: [],
    documents: [],
    rememberMe: false,
});

const defaultAuthState = createDefaultAuthState();

const getStorageArea = (driver: StorageDriver) =>
    driver === 'local' ? localStorage : sessionStorage;

const parseJSON = <T>(value: string | object | null): T | null => {
    if (!value) {
        return null;
    }
    if (typeof value === 'string') {
        try {
            return JSON.parse(value) as T;
        } catch {
            return null;
        }
    }
    return value as T;
};

const detectDriver = (): StorageDriver | null => {
    if (storage.get(localStorage, LOCALSTORAGE.accessToken)) {
        return 'local';
    }
    if (storage.get(sessionStorage, LOCALSTORAGE.accessToken)) {
        return 'session';
    }
    return null;
};

const clearAuthKeys = (driver: StorageDriver) => {
    const area = getStorageArea(driver);
    AUTH_KEYS.forEach((key) => {
        storage.remove(area, key);
    });
};

const clearAllAuthData = () => {
    clearAuthKeys('local');
    clearAuthKeys('session');
    
  // 🔹 Clear React Query cache
  queryClient.cancelQueries();
  queryClient.clear();
};

const persistAuthSnapshot = (authState: AuthState, driver: StorageDriver) => {
    const area = getStorageArea(driver);
    storage.set(area, LOCALSTORAGE.accessToken, authState.accessToken ?? '');
    storage.set(area, LOCALSTORAGE.refreshToken, authState.refreshToken ?? '');
    storage.set(area, LOCALSTORAGE.user, JSON.stringify(authState.user));
    storage.set(area, LOCALSTORAGE.menu, JSON.stringify(authState.menu));
    storage.set(area, LOCALSTORAGE.documents, JSON.stringify(authState.documents));
};

const persistDocuments = (documents: DocumentPropsTypes[], driver: StorageDriver) => {
    const area = getStorageArea(driver);
    storage.set(area, LOCALSTORAGE.documents, JSON.stringify(documents));
};

const persistMenu = (menu: AuthState['menu'], driver: StorageDriver) => {
    const area = getStorageArea(driver);
    storage.set(area, LOCALSTORAGE.menu, JSON.stringify(menu));
};

const readAuthSnapshot = (driver: StorageDriver): Partial<AuthState> => {
    const area = getStorageArea(driver);
    const accessToken = storage.get(area, LOCALSTORAGE.accessToken) as string | null;
    const refreshToken = storage.get(area, LOCALSTORAGE.refreshToken) as string | null;
    const user = parseJSON<AuthState['user']>(storage.get(area, LOCALSTORAGE.user));
    const menu = parseJSON<AuthState['menu']>(storage.get(area, LOCALSTORAGE.menu)) ?? undefined;
    const documents = parseJSON<AuthState['documents']>(storage.get(area, LOCALSTORAGE.documents)) ?? undefined;

    return {
        accessToken,
        refreshToken,
        user,
        menu,
        documents,
    };
};

interface AuthStoreState {
    authState: AuthState;
    loading: boolean;
    hydrated: boolean;
    storageDriver: StorageDriver;
    hydrate: (options?: HydrateOptions) => Promise<void>;
    login: (
        username: string,
        password: string,
        appType: string,
        rememberMe: boolean,
    ) => Promise<boolean>;
    logout: () => Promise<void>;
    syncDocuments: (documents: DocumentPropsTypes[]) => void;
    syncMenu: (menu: AuthState['menu']) => void;
    updateUser: (user: AuthState['user']) => void;
}

export const useAuthStore = create<AuthStoreState>((set, get) => ({
    authState: defaultAuthState,
    loading: true,
    hydrated: false,
    storageDriver: 'session',

    hydrate: async (options?: HydrateOptions) => {
        if (!options?.silent) {
            set({ loading: true });
        }
        try {
            const driver = detectDriver();
            if (!driver) {
                clearAllAuthData();
                set({
                    authState: createDefaultAuthState(),
                    storageDriver: 'session',
                    hydrated: true,
                    loading: false,
                });
                return;
            }

            const snapshot = readAuthSnapshot(driver);
            if (snapshot.accessToken && snapshot.user && snapshot.menu) {
                set({
                    authState: {
                        ...createDefaultAuthState(),
                        ...snapshot,
                        documents: snapshot.documents ?? [],
                        rememberMe: driver === 'local',
                        isAuthenticated: true,
                    },
                    storageDriver: driver,
                    hydrated: true,
                    loading: false,
                });
            } else {
                clearAllAuthData();
                set({
                    authState: createDefaultAuthState(),
                    storageDriver: 'session',
                    hydrated: true,
                    loading: false,
                });
            }
        } catch (error) {
            console.error('Failed to hydrate auth store', error);
            clearAllAuthData();
            set({
                authState: createDefaultAuthState(),
                storageDriver: 'session',
                hydrated: true,
                loading: false,
            });
        }
    },

    login: async (username, password, appType, rememberMe) => {
        set({ loading: true });
        try {
            const data = await authService().login(username, password, appType);
            if (!data) {
                throw new Error('Invalid login response');
            }
            // This will be populated via syncDocuments and syncMenu after login
            const authData: AuthState = {
                isAuthenticated: true,
                accessToken: data.accessToken ?? null,
                refreshToken: data.refreshToken ?? null,
                user: data.user ?? null,
                menu: [],
                documents: [],
                rememberMe,
            };

            const driver: StorageDriver = rememberMe ? 'local' : 'session';
            clearAuthKeys(driver === 'local' ? 'session' : 'local');
            persistAuthSnapshot(authData, driver);

            set({
                authState: authData,
                storageDriver: driver,
                hydrated: true,
                loading: false,
            });

            return true;
        } catch (error) {
            console.error('Login failed', error);
            clearAllAuthData();
            set({
                authState: createDefaultAuthState(),
                storageDriver: 'session',
                loading: false,
            });
            return false;
        }
    },

    logout: async () => {
        set({ loading: true });
        try {
            await authService().logout();
        } catch (error) {
            console.error('Logout failed', error);
        } finally {
            clearAllAuthData();
            set({
                authState: createDefaultAuthState(),
                storageDriver: 'session',
                loading: false,
                hydrated: true,
            });
        }
    },

    syncDocuments: (documents) => {
        set((state) => ({
            authState: {
                ...state.authState,
                documents,
            },
        }));

        try {
            persistDocuments(documents, get().storageDriver);
        } catch (error) {
            console.error('Failed to persist documents', error);
        }
    },
    syncMenu: (menu) => {
        set((state) => ({
            authState: {
                ...state.authState,
                menu,
            },
        }));

        try {
            persistMenu(menu, get().storageDriver);
        } catch (error) {
            console.error('Failed to persist menu', error);
        }
    },
    updateUser: (user) => {
        set((state) => {
            const updatedAuthState = {
                ...state.authState,
                user,
            };
            try {
                persistAuthSnapshot(updatedAuthState, state.storageDriver);
            } catch (error) {
                console.error('Failed to persist user update', error);
            }
            return { authState: updatedAuthState };
        });
    },
}));

